/**
 * DenseSkillEffect — Astral dense-path gravitational mass buff.
 *
 * A 5-second timed toggle that cranks the player's gravitational mass:
 * 1. Knockback-immune for the duration.
 * 2. Enemies within 1.5-tile proximity stagger on contact.
 * 3. Phase Step leaves a gravity wake (slow zone) at the departure point.
 * 4. Movement speed reduced 30%.
 *
 * Upgrade hooks:
 * - dense_knockback (Mass Differential): `contactKnockbackTiles` (additive, scope: dense)
 *   — adds 0.5-tile knockback to proximity staggers.
 * - dense_duration (Sustained Mass): `durationMs` (additive, scope: dense) — extends
 *   Dense window by 2000ms (5s → 7s); `gravityWakeDurationMs` (additive, scope: dense)
 *   — extends gravity wake by 300ms (500ms → 800ms).
 *
 * Collapse (replacesId: dense) inherits this class's structure. Not marked final.
 *
 * Phase sequence: active window (5000ms, extendable) → medium cooldown (1200ms).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § dense
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy and immunity windows)
 */

import { ToggleSkillEffect } from '../../ToggleSkillEffect.js';
import type { TeleportEvent } from '../../../systems/AstralTeleportSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface DenseDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly statusEffects: {
    apply(statusId: string, ...args: unknown[]): void;
    remove(statusId: string, ...args: unknown[]): void;
    isImmune(statusId: string): boolean;
  };
  readonly zones: {
    create(config: {
      id: string;
      type: string;
      position: { x: number; y: number };
      durationMs: number;
      sourceSkillId: string;
      shape: 'circle';
      radiusTiles: number;
      speedMultiplier?: number;
    }): string;
  };
  readonly teleport: {
    onTeleport(callback: (event: TeleportEvent) => void): void;
  };
  /** Applies radial knockback away from the player. Game layer wires this to KnockbackSystem. */
  readonly knockback: {
    apply(tiles: number): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface DenseConfig {
  /** Base window duration in ms (5000). */
  readonly baseWindowMs: number;
  /** Medium cooldown tier in ms (1200). */
  readonly cooldownMs: number;
  /** Movement speed multiplier while active (0.7 = 30% slower). */
  readonly movementSpeedMultiplier: number;
  /** Proximity stagger radius in tiles (1.5). */
  readonly staggerRadiusTiles: number;
  /** Stagger duration applied to enemies on proximity contact in ms. */
  readonly staggerDurationMs: number;
  /** Base gravity wake zone duration in ms (500). */
  readonly baseGravityWakeMs: number;
  /** Gravity wake slow zone radius in tiles. */
  readonly gravityWakeRadiusTiles: number;
  /** Gravity wake speed multiplier (0.5 = 50% slow). */
  readonly gravityWakeSpeedMultiplier: number;
}

const DEFAULT_CONFIG: DenseConfig = {
  baseWindowMs: 5000,
  cooldownMs: 1200,                // medium cooldown tier
  movementSpeedMultiplier: 0.7,    // 30% speed penalty
  staggerRadiusTiles: 1.5,
  staggerDurationMs: 200,          // 0.2s stagger on contact
  baseGravityWakeMs: 500,
  gravityWakeRadiusTiles: 1.5,
  gravityWakeSpeedMultiplier: 0.5, // 50% slow
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class DenseSkillEffect extends ToggleSkillEffect {
  readonly skillId: string = 'dense';

  protected readonly deps: DenseDeps;
  protected readonly config: DenseConfig;

  /** Remaining ms of the current Dense window. */
  private _windowRemaining = 0;

  /** Resolved window duration (base + dense_duration bonus). */
  private _resolvedWindowMs = 0;

  /** Whether the dense_active buff is currently applied. */
  private _buffApplied = false;

  /** Total staggers applied during the current activation. */
  private _staggersApplied = 0;

  /** Total gravity wake zones spawned during the current activation. */
  private _wakesSpawned = 0;

  /** Total activations across the skill's lifetime. */
  private _activationsCount = 0;

  /** Running ID counter for gravity wake zones. */
  private _wakeIdCounter = 0;

  constructor(deps: DenseDeps, config: Partial<DenseConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Register teleport listener at construction time.
    // AstralTeleportSystem has no unregister API — lives for the session.
    deps.teleport.onTeleport(this._onTeleport.bind(this));
  }

  // --- State getters ---

  get windowRemaining(): number { return this._windowRemaining; }
  get resolvedWindowMs(): number { return this._resolvedWindowMs; }
  get buffApplied(): boolean { return this._buffApplied; }
  get staggersApplied(): number { return this._staggersApplied; }
  get wakesSpawned(): number { return this._wakesSpawned; }
  get activationsCount(): number { return this._activationsCount; }

  // ---------------------------------------------------------------------------
  // ToggleSkillEffect implementation
  // ---------------------------------------------------------------------------

  canActivate(): boolean {
    return !this._isActive && this.cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Resolve window: base + dense_duration additive bonus.
    const durationBonus = this.deps.resolver.getAdditiveEffect('dense', 'durationMs');
    this._resolvedWindowMs = this.config.baseWindowMs + durationBonus;
    this._windowRemaining = this._resolvedWindowMs;

    this._isActive = true;
    this._staggersApplied = 0;
    this._wakesSpawned = 0;
    this._activationsCount++;

    // Apply buff: knockback immunity + movement penalty.
    this.deps.statusEffects.apply(
      'dense_active',
      -1, // permanent until deactivated
      {
        knockbackImmune: true,
        movementSpeedMultiplier: this.config.movementSpeedMultiplier,
      },
      'dense',
    );
    this._buffApplied = true;

    return true;
  }

  protected override updateWhileActive(delta: number): void {
    this._windowRemaining -= delta;
    if (this._windowRemaining <= 0) {
      this._windowRemaining = 0;
      this.deactivate();
    }
  }

  protected override onDeactivate(): void {
    this._removeBuff();
    this.cooldownRemaining = this.config.cooldownMs;
  }

  override destroy(): void {
    this._removeBuff();
    super.destroy();
  }

  // ---------------------------------------------------------------------------
  // External event handlers
  // ---------------------------------------------------------------------------

  /**
   * Called by the game layer when an enemy enters the proximity field
   * (within staggerRadiusTiles) while Dense is active. Applies stagger
   * and optional knockback (if Mass Differential is unlocked).
   *
   * Returns true if stagger was applied, false if Dense is inactive or
   * the enemy is immune.
   */
  handleProximityContact(): boolean {
    if (!this._isActive) return false;

    // Check stagger immunity (ADR-065: 500ms window post-expiry).
    if (this.deps.statusEffects.isImmune('stagger')) return false;

    this.deps.statusEffects.apply('stagger', this.config.staggerDurationMs, 'dense');
    this._staggersApplied++;

    // Mass Differential upgrade — apply radial knockback on stagger contact.
    const knockbackTiles = this.getContactKnockbackTiles();
    if (knockbackTiles > 0) {
      this.deps.knockback.apply(knockbackTiles);
    }

    return true;
  }

  /**
   * Returns the knockback distance in tiles for Mass Differential.
   * Zero if the upgrade is not unlocked.
   */
  getContactKnockbackTiles(): number {
    return this.deps.resolver.getAdditiveEffect('dense', 'contactKnockbackTiles');
  }

  // ---------------------------------------------------------------------------
  // Teleport listener — gravity wake
  // ---------------------------------------------------------------------------

  private _onTeleport(event: TeleportEvent): void {
    if (!this._isActive) return;

    // Resolve wake duration: base + dense_duration bonus.
    const wakeBonus = this.deps.resolver.getAdditiveEffect('dense', 'gravityWakeDurationMs');
    const wakeDuration = this.config.baseGravityWakeMs + wakeBonus;

    const zoneId = `dense_wake_${this._wakeIdCounter++}`;
    this.deps.zones.create({
      id: zoneId,
      type: 'gravity_wake',
      position: { x: event.fromX, y: event.fromY },
      durationMs: wakeDuration,
      sourceSkillId: 'dense',
      shape: 'circle',
      radiusTiles: this.config.gravityWakeRadiusTiles,
      speedMultiplier: this.config.gravityWakeSpeedMultiplier,
    });
    this._wakesSpawned++;
  }

  // ---------------------------------------------------------------------------
  // Internal
  // ---------------------------------------------------------------------------

  private _removeBuff(): void {
    if (this._buffApplied) {
      this.deps.statusEffects.remove('dense_active', 'dense');
      this._buffApplied = false;
    }
  }
}
